<template>
    <div class="row">
        <div class="col-md-8 mb-5">

            <!-- Card Coupon -->
            <div class="card mt-5">
                <div class="card-body">
                    <div id="accordion" class="accordion">
                        <div id="TabCoupon" class="card-header">
                            <h2 class="h4 card-header-title"
                                @click="collapseToggle('Coupon')"
                                aria-expanded="true"
                                aria-controls="collapseCoupon"
                                data-toggle="collapse"
                                data-target="#collapseCoupon">Coupon
                                <span id="iconToggleCoupon"
                                    class="ti-angle-up u-sidebar-nav-menu__item-arrow pull-right black">
                                </span>
                            </h2>
                        </div>
                        <div id="collapseCoupon"
                            class="collapse show"
                            aria-labelledby="TabCoupon"
                            data-parent="#accordion">

                            <div class="col-12 pt-3">

                                <!-- Code -->
                                <div class="form-group">
                                    <label for="inputCode">Code <span class="text-danger">*</span></label>
                                    <input class="form-control text-uppercase"
                                        :class="{ 'is-invalid': fieldError('code') }"
                                        id="inputCode"
                                        type="text"
                                        maxlength="64"
                                        placeholder="e.g. SAHARA10"
                                        :value="row.code"
                                        @input="onCodeInput"
                                        required>
                                    <div v-if="fieldError('code')" class="invalid-feedback d-block">
                                        {{ fieldError('code') }}
                                    </div>
                                    <small v-if="!fieldError('code') && codeExists" class="form-text text-warning">
                                        <span class="ti-alert"></span>
                                        A coupon with this code already exists for this tenant.
                                    </small>
                                    <small v-if="!fieldError('code') && !codeExists" class="form-text text-muted">
                                        Case-insensitive for travellers. Saved in UPPERCASE with spaces removed. Max 64 characters.
                                    </small>
                                </div>
                                <!-- End Code -->

                                <!-- Name -->
                                <div class="form-group">
                                    <label for="inputName">Name <small class="text-muted">(internal label)</small></label>
                                    <input class="form-control"
                                        :class="{ 'is-invalid': fieldError('name') }"
                                        id="inputName"
                                        type="text"
                                        maxlength="255"
                                        placeholder="e.g. Sahara 10% Launch Promo"
                                        v-model="row.name">
                                    <div v-if="fieldError('name')" class="invalid-feedback d-block">
                                        {{ fieldError('name') }}
                                    </div>
                                </div>
                                <!-- End Name -->

                                <div class="row">
                                    <!-- Discount Type -->
                                    <div class="form-group col-md-6">
                                        <label>Discount Type <span class="text-danger">*</span></label>
                                        <div class="d-flex">
                                            <div class="custom-control custom-radio mr-4">
                                                <input type="radio"
                                                    id="discountPercentage"
                                                    class="custom-control-input"
                                                    value="percentage"
                                                    v-model="row.discount_type">
                                                <label class="custom-control-label" for="discountPercentage">
                                                    Percentage
                                                </label>
                                            </div>
                                            <div class="custom-control custom-radio">
                                                <input type="radio"
                                                    id="discountFixed"
                                                    class="custom-control-input"
                                                    value="fixed"
                                                    v-model="row.discount_type">
                                                <label class="custom-control-label" for="discountFixed">
                                                    Fixed amount
                                                </label>
                                            </div>
                                        </div>
                                        <div v-if="fieldError('discount_type')" class="invalid-feedback d-block">
                                            {{ fieldError('discount_type') }}
                                        </div>
                                    </div>
                                    <!-- End Discount Type -->

                                    <!-- Discount Value -->
                                    <div class="form-group col-md-6">
                                        <label for="inputDiscountValue">Discount Value <span class="text-danger">*</span></label>
                                        <div class="input-group">
                                            <input class="form-control"
                                                :class="{ 'is-invalid': fieldError('discount_value') }"
                                                id="inputDiscountValue"
                                                type="number"
                                                step="0.01"
                                                min="0.01"
                                                :max="(row.discount_type == 'percentage') ? 100 : null"
                                                v-model.number="row.discount_value"
                                                required>
                                            <div class="input-group-append">
                                                <span class="input-group-text">
                                                    {{ (row.discount_type == 'percentage') ? '%' : currency }}
                                                </span>
                                            </div>
                                        </div>
                                        <div v-if="fieldError('discount_value')" class="invalid-feedback d-block">
                                            {{ fieldError('discount_value') }}
                                        </div>
                                        <small v-if="!fieldError('discount_value')" class="form-text text-muted">
                                            <span v-if="row.discount_type == 'percentage'">Percent off the booking total. Cannot exceed 100.</span>
                                            <span v-if="row.discount_type == 'fixed'">Fixed amount off the booking total. Never takes the total below zero.</span>
                                        </small>
                                    </div>
                                    <!-- End Discount Value -->
                                </div>

                                <div class="row">
                                    <!-- Min Booking Value -->
                                    <div class="form-group col-md-6">
                                        <label for="inputMinBooking">Minimum Booking Value</label>
                                        <div class="input-group">
                                            <input class="form-control"
                                                :class="{ 'is-invalid': fieldError('min_booking_value') }"
                                                id="inputMinBooking"
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                placeholder="No minimum"
                                                v-model="row.min_booking_value">
                                            <div class="input-group-append">
                                                <span class="input-group-text">{{ currency }}</span>
                                            </div>
                                        </div>
                                        <div v-if="fieldError('min_booking_value')" class="invalid-feedback d-block">
                                            {{ fieldError('min_booking_value') }}
                                        </div>
                                        <small v-if="!fieldError('min_booking_value')" class="form-text text-muted">
                                            Booking original price must be at least this much. Leave empty for no minimum.
                                        </small>
                                    </div>
                                    <!-- End Min Booking Value -->

                                    <!-- Usage Limit -->
                                    <div class="form-group col-md-6">
                                        <label for="inputUsageLimit">Usage Limit</label>
                                        <input class="form-control"
                                            :class="{ 'is-invalid': fieldError('usage_limit') }"
                                            id="inputUsageLimit"
                                            type="number"
                                            step="1"
                                            min="1"
                                            placeholder="Unlimited"
                                            v-model="row.usage_limit">
                                        <div v-if="fieldError('usage_limit')" class="invalid-feedback d-block">
                                            {{ fieldError('usage_limit') }}
                                        </div>
                                        <small v-if="!fieldError('usage_limit')" class="form-text text-muted">
                                            Max number of bookings that can redeem it. Leave empty for unlimited.
                                        </small>
                                    </div>
                                    <!-- End Usage Limit -->
                                </div>

                            </div>

                        </div>
                    </div>
                </div>
            </div>
            <!-- End Card Coupon -->

            <!-- Card Validity -->
            <div class="card mt-5">
                <div class="card-body">
                    <div id="accordionValidity" class="accordion">
                        <div id="TabValidity" class="card-header">
                            <h2 class="h4 card-header-title"
                                @click="collapseToggle('Validity')"
                                aria-expanded="true"
                                aria-controls="collapseValidity"
                                data-toggle="collapse"
                                data-target="#collapseValidity">Validity Period
                                <span id="iconToggleValidity"
                                    class="ti-angle-up u-sidebar-nav-menu__item-arrow pull-right black">
                                </span>
                            </h2>
                        </div>
                        <div id="collapseValidity"
                            class="collapse show"
                            aria-labelledby="TabValidity"
                            data-parent="#accordionValidity">

                            <div class="col-12 pt-3">
                                <div class="row">
                                    <!-- Valid From -->
                                    <div class="form-group col-md-6">
                                        <label for="inputValidFrom">Valid From</label>
                                        <input class="form-control"
                                            :class="{ 'is-invalid': fieldError('valid_from') }"
                                            id="inputValidFrom"
                                            type="date"
                                            v-model="row.valid_from">
                                        <div v-if="fieldError('valid_from')" class="invalid-feedback d-block">
                                            {{ fieldError('valid_from') }}
                                        </div>
                                        <small v-if="!fieldError('valid_from')" class="form-text text-muted">
                                            Not usable before this date. Leave empty to start immediately.
                                        </small>
                                    </div>
                                    <!-- End Valid From -->

                                    <!-- Valid To -->
                                    <div class="form-group col-md-6">
                                        <label for="inputValidTo">Valid To</label>
                                        <input class="form-control"
                                            :class="{ 'is-invalid': fieldError('valid_to') }"
                                            id="inputValidTo"
                                            type="date"
                                            :min="row.valid_from || null"
                                            v-model="row.valid_to">
                                        <div v-if="fieldError('valid_to')" class="invalid-feedback d-block">
                                            {{ fieldError('valid_to') }}
                                        </div>
                                        <small v-if="!fieldError('valid_to')" class="form-text text-muted">
                                            Expiry date. Must be on or after Valid From. Leave empty for no expiry.
                                        </small>
                                    </div>
                                    <!-- End Valid To -->
                                </div>

                                <div v-if="isExpiredNow" class="alert alert-warning f13 mb-0">
                                    <span class="ti-alert mr-1"></span>
                                    The Valid To date is in the past. This coupon will be rejected at checkout as expired.
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
            <!-- End Card Validity -->

        </div>


        <!-- ******* SideNavbar ******** -->
        <div class="col-md-4 mb-5">

            <!-- Nav Packages -->
            <div class="card mt-5">
                <div class="card-body">
                    <div id="accordionNav" class="accordion">
                        <div id="NavPackages" class="card-header">
                            <h2 class="h4 card-header-title"
                                @click="collapseToggle('Packages')"
                                aria-expanded="true"
                                aria-controls="collapseNavPackages"
                                data-toggle="collapse"
                                data-target="#collapseNavPackages">Applies To
                                <span id="iconTogglePackages"
                                    class="ti-angle-up u-sidebar-nav-menu__item-arrow pull-right black">
                                </span>
                            </h2>
                        </div>
                        <div id="collapseNavPackages"
                            class="collapse show"
                            aria-labelledby="NavPackages"
                            data-parent="#accordionNav">

                            <div class="col-12 pt-3">
                                <div class="form-group">
                                    <label>Packages</label>
                                    <div v-if="packageLoading" class="text-center">
                                        <span class="spinner-grow spinner-grow-sm mr-1"
                                            role="status"
                                            aria-hidden="true">
                                        </span>
                                    </div>
                                    <multiselect v-if="!packageLoading"
                                            id="multiselectPackages"
                                            v-model="row.packagesValues"
                                            :options="packagesOptions"
                                            label="title"
                                            track-by="id"
                                            :multiple="true"
                                            :close-on-select="false"
                                            :clear-on-select="false"
                                            :hide-selected="true"
                                            :preserve-search="true"
                                            :taggable="false"
                                            placeholder="Type to search packages"
                                            :preselect-first="false">
                                    </multiselect>
                                    <div v-if="fieldError('package_ids')" class="invalid-feedback d-block">
                                        {{ fieldError('package_ids') }}
                                    </div>
                                    <small class="form-text mt-2"
                                        :class="(row.packagesValues.length) ? 'text-warning' : 'text-success'">
                                        <span v-if="!row.packagesValues.length">
                                            <span class="ti-check mr-1"></span>Applies to <b>all packages</b>.
                                        </span>
                                        <span v-if="row.packagesValues.length">
                                            <span class="ti-lock mr-1"></span>Restricted to
                                            <b>{{ row.packagesValues.length }}</b>
                                            {{ (row.packagesValues.length == 1) ? 'package' : 'packages' }}.
                                            Rejected on any other package.
                                        </span>
                                    </small>
                                    <a v-if="row.packagesValues.length"
                                        href="javascript:;"
                                        class="f12"
                                        @click="row.packagesValues = []">Clear selection (apply to all)</a>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
            <!-- End Nav Packages -->

            <!-- Nav Status -->
            <div class="card mt-5">
                <div class="card-body">
                    <div id="accordionNavStatus" class="accordion">
                        <div id="NavStatus" class="card-header">
                            <h2 class="h4 card-header-title"
                                @click="collapseToggle('Status')"
                                aria-expanded="true"
                                aria-controls="collapseNavStatus"
                                data-toggle="collapse"
                                data-target="#collapseNavStatus">Status
                                <span id="iconToggleStatus"
                                    class="ti-angle-up u-sidebar-nav-menu__item-arrow pull-right black">
                                </span>
                            </h2>
                        </div>
                        <div id="collapseNavStatus"
                            class="collapse show"
                            aria-labelledby="NavStatus"
                            data-parent="#accordionNavStatus">
                            <div class="col-12 pt-3">
                                <!-- Status -->
                                <div class="form-group">
                                    <div class="custom-control custom-switch mb-2">
                                        <input type="checkbox"
                                            class="custom-control-input"
                                            id="customSwitchStatus"
                                            :checked="row.status"
                                            @click="onStatus">
                                        <label class="custom-control-label"
                                            for="customSwitchStatus"
                                            v-html="(row.status) ? 'Active' : 'Inactive'">
                                        </label>
                                    </div>
                                    <small class="form-text text-muted">
                                        Inactive coupons are rejected at checkout.
                                    </small>
                                    <div v-if="fieldError('status')" class="invalid-feedback d-block">
                                        {{ fieldError('status') }}
                                    </div>
                                </div>
                                <!-- End Status -->
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <!-- End Nav Status -->

            <!-- Preview -->
            <div class="card mt-5">
                <div class="card-body">
                    <h2 class="h4 card-header-title mb-3">Summary Preview</h2>
                    <p class="f13 text-muted mb-2">
                        How the discount looks on a sample
                        <input type="number" class="form-control form-control-sm d-inline-block"
                            style="width: 110px" min="0" step="1" v-model.number="sampleTotal">
                        {{ currency }} booking:
                    </p>
                    <table class="table table-sm f13 mb-0">
                        <tbody>
                            <tr>
                                <td>Original Price</td>
                                <td class="text-right">{{ money(sampleTotal) }}</td>
                            </tr>
                            <tr>
                                <td>Discount / Promo Code</td>
                                <td class="text-right text-uppercase">
                                    {{ row.code || '—' }}
                                    <span v-if="row.discount_value">({{ discountLabel }})</span>
                                </td>
                            </tr>
                            <tr>
                                <td>Discount Amount</td>
                                <td class="text-right text-danger">−{{ money(previewDiscount) }}</td>
                            </tr>
                            <tr class="font-weight-bold">
                                <td>Final Total</td>
                                <td class="text-right">{{ money(sampleTotal - previewDiscount) }}</td>
                            </tr>
                        </tbody>
                    </table>
                    <small v-if="belowMinimum" class="form-text text-warning mt-2">
                        <span class="ti-alert"></span>
                        This sample is below the minimum booking value, so the coupon would be rejected.
                    </small>
                </div>
            </div>
            <!-- End Preview -->

        </div>
    </div>
</template>

<script>
    import Multiselect from 'vue-multiselect';

    export default {
        name: 'CouponForm',
        components: {
            Multiselect
        },
        props: {
            row: { type: Object, required: true },
            errors: { type: Object, default: () => ({}) },
            tenantId: { type: [Number, String], default: 0 },
            existingCodes: { type: Array, default: () => [] },
            originalCode: { type: String, default: '' },
            currency: { type: String, default: 'USD' },
        },
        data(){
            return {
                auth: {
                    access_token: '',
                },
                clientErrors: {},
                packagesOptions: [],
                packageLoading: true,
                sampleTotal: 1000,
            }
        },
        computed: {
            codeExists() {
                if(!this.row.code) { return false; }
                const code = this.normalizeCode(this.row.code);
                if(this.originalCode && code == this.normalizeCode(this.originalCode)) { return false; }
                return this.existingCodes.some(c => this.normalizeCode(c) == code);
            },
            isExpiredNow() {
                if(!this.row.valid_to) { return false; }
                return this.row.valid_to < this.today();
            },
            discountLabel() {
                if(this.row.discount_type == 'percentage') {
                    return this.row.discount_value + '%';
                }
                return this.money(this.row.discount_value) + ' ' + this.currency;
            },
            previewDiscount() {
                const total = Number(this.sampleTotal) || 0;
                const value = Number(this.row.discount_value) || 0;
                if(this.belowMinimum) { return 0; }
                let amount = (this.row.discount_type == 'percentage') ? total * value / 100 : value;
                return Math.min(amount, total);
            },
            belowMinimum() {
                const min = this.row.min_booking_value;
                if(min === null || min === '' || min === undefined) { return false; }
                return (Number(this.sampleTotal) || 0) < Number(min);
            },
        },
        watch: {
            tenantId() {
                this.fetchPackages();
            },
            row: {
                deep: true,
                handler() {
                    // clear client-side errors as the user edits
                    if(Object.keys(this.clientErrors).length) { this.clientErrors = {}; }
                }
            }
        },
        created() {
            if(localStorage.getItem('access_token')) {
                this.auth.access_token = localStorage.getItem('access_token');
            }
            this.fetchPackages();
        },
        methods: {

            today() {
                const d = new Date();
                const m = String(d.getMonth() + 1).padStart(2, '0');
                const day = String(d.getDate()).padStart(2, '0');
                return d.getFullYear() + '-' + m + '-' + day;
            },

            money(v) {
                const n = Number(v) || 0;
                return n.toFixed(2);
            },

            normalizeCode(code) {
                return String(code || '').toUpperCase().replace(/\s+/g, '');
            },

            onCodeInput(e) {
                const normalized = this.normalizeCode(e.target.value);
                this.row.code = normalized;
                // keep the caret sane when characters were stripped
                e.target.value = normalized;
            },

            onStatus(){
                this.row.status = this.row.status ? 0 : 1;
            },

            fieldError(field) {
                if(this.clientErrors[field]) { return this.clientErrors[field]; }
                const serverErrors = this.errors || {};
                // exact match, or nested match like package_ids.0
                const key = Object.keys(serverErrors).find(k => k == field || k.indexOf(field + '.') === 0);
                if(!key) { return ''; }
                const val = serverErrors[key];
                return Array.isArray(val) ? val[0] : val;
            },

            // Client-side validation mirroring the API rules
            validate() {
                const e = {};
                const r = this.row;

                if(!r.code || !this.normalizeCode(r.code).length) {
                    e.code = 'The code field is required.';
                } else if(this.normalizeCode(r.code).length > 64) {
                    e.code = 'The code may not be greater than 64 characters.';
                }

                if(r.name && r.name.length > 255) {
                    e.name = 'The name may not be greater than 255 characters.';
                }

                if(r.discount_type != 'percentage' && r.discount_type != 'fixed') {
                    e.discount_type = 'Select a discount type.';
                }

                const dv = Number(r.discount_value);
                if(r.discount_value === '' || r.discount_value === null || isNaN(dv) || dv <= 0) {
                    e.discount_value = 'The discount value must be greater than 0.';
                } else if(r.discount_type == 'percentage' && dv > 100) {
                    e.discount_value = 'A percentage discount cannot exceed 100.';
                }

                if(r.valid_from && r.valid_to && r.valid_to < r.valid_from) {
                    e.valid_to = 'Valid To must be a date after or equal to Valid From.';
                }

                if(r.min_booking_value !== '' && r.min_booking_value !== null && r.min_booking_value !== undefined) {
                    const mb = Number(r.min_booking_value);
                    if(isNaN(mb) || mb < 0) {
                        e.min_booking_value = 'The minimum booking value must be 0 or more.';
                    }
                }

                if(r.usage_limit !== '' && r.usage_limit !== null && r.usage_limit !== undefined) {
                    const ul = Number(r.usage_limit);
                    if(isNaN(ul) || ul < 1 || Math.floor(ul) !== ul) {
                        e.usage_limit = 'The usage limit must be a whole number of at least 1.';
                    }
                }

                this.clientErrors = e;
                return Object.keys(e).length === 0;
            },

            // Build the request body exactly as the API expects it
            buildPayload() {
                const r = this.row;
                const nullable = (v) => (v === '' || v === undefined) ? null : v;
                return {
                    tenant_id: this.tenantId,
                    code: this.normalizeCode(r.code),
                    name: nullable(r.name),
                    discount_type: r.discount_type,
                    discount_value: Number(r.discount_value),
                    valid_from: nullable(r.valid_from),
                    valid_to: nullable(r.valid_to),
                    min_booking_value: (nullable(r.min_booking_value) === null) ? null : Number(r.min_booking_value),
                    usage_limit: (nullable(r.usage_limit) === null) ? null : parseInt(r.usage_limit, 10),
                    package_ids: (r.packagesValues || []).map(p => p.id),
                    status: r.status ? true : false,
                };
            },

            // fetch Packages for the multi-select
            fetchPackages(){
                if(!this.tenantId || this.tenantId == 0) {
                    this.packageLoading = false;
                    this.packagesOptions = [];
                    return;
                }
                this.packageLoading = true;
                this.axios.defaults.headers.common = {
                    'X-Requested-With': 'XMLHttpRequest', // security to prevent CSRF attacks
                    'Authorization': `Bearer ` + this.auth.access_token,
                };
                const options = {
                    url: window.baseURL+'/packages',
                    method: 'GET',
                    data: {},
                    params: {
                        tenant_id: this.tenantId,
                        status: 'active',
                        paginate: 100,
                    },
                }
                this.axios(options)
                    .then(res => {
                        this.packageLoading = false;
                        this.packagesOptions = (res.data.rows || []).map(p => ({
                            id: p.id,
                            title: p.title,
                            slug: p.slug,
                        }));
                        // make sure already-selected packages (edit mode) are selectable even if inactive
                        (this.row.packagesValues || []).forEach(sel => {
                            if(!this.packagesOptions.some(o => o.id == sel.id)) {
                                this.packagesOptions.push(sel);
                            }
                        });
                    })
                    .catch(() => {
                        this.packageLoading = false;
                    })
                    .finally(() => {});
            },

            // toggleCollapse
            collapseToggle(div) {
                let el = document.querySelector("span#iconToggle"+div);
                if(!el) { return; }
                if(el.classList.contains('ti-angle-down')) {
                    el.classList.remove('ti-angle-down');
                    el.classList.add('ti-angle-up');
                } else {
                    el.classList.remove('ti-angle-up');
                    el.classList.add('ti-angle-down');
                }
            },
        },
    }
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
